import { FC, ReactNode, useContext } from 'react'

import { GRAY_900 } from 'constants/styling/theme'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material'
import Typography from '@mui/material/Typography'
import { EMBED_CHROME_SELECTOR, EMBED_STEP_CONTENT_PADDING_X, STEP_CONTENT_PADDING_X } from 'components/pages/PurchaseFlow/_main/purchaseFlowLayout'

import { StepWrapperEmbeddedContext } from './StepWrapperEmbeddedContext'

/** Props for the StepWrapper component. */
interface Props {
  /** The content to be wrapped by the StepWrapper component.  */
  children: ReactNode
  /** The main title displayed in the step wrapper. */
  title: ReactNode
  /** Style options for page title */
  titleSx?: SxProps
  /** An optional subtitle displayed below the main title. */
  subtitle?: string
  /** Optional element id */
  id?: string
  /**
   * When `true`, skip rendering the H1 + subtitle block. Falls back to
   * `StepWrapperEmbeddedContext.hideTitle` when not provided.
   * Default `false` — legacy `/purchase-flow` flow unaffected.
   */
  hideTitle?: boolean
  /**
   * When `true`, drop the outer horizontal step padding. Falls back to
   * `StepWrapperEmbeddedContext.noPadding` when not provided.
   * Default `false` — legacy `/purchase-flow` flow unaffected.
   */
  noPadding?: boolean
}

/**
 * @component
 * StepWrapper component wraps its children with a standardized layout for step components.
 *
 * @example
 * <StepWrapper
 *   title="That's what she said!"
 *   subtitle="Said Michael Scott during a meeting at Dunder Mifflin."
 * >
 *   <StepContent />
 * </StepWrapper>
 */
export const StepWrapper: FC<Props> = ({ title, titleSx, subtitle, children, id, hideTitle, noPadding }) => {
  const ctx = useContext(StepWrapperEmbeddedContext)
  const resolvedHideTitle = hideTitle ?? ctx.hideTitle
  const resolvedNoPadding = noPadding ?? ctx.noPadding

  return (
    <Stack
      id={id} // Part of a temporary solution to allow the FloatingScrollButton to work properly
      sx={{
        paddingBottom: '4rem',
        height: '100%',
        // Temporary solution - allows the content to be scrollable if it exceeds the height for the Property Information step
        overflow: 'auto',
        paddingX: resolvedNoPadding ? 0 : STEP_CONTENT_PADDING_X,
        // A step that asked for no padding keeps none inside the embed either.
        [EMBED_CHROME_SELECTOR]: { paddingX: resolvedNoPadding ? 0 : EMBED_STEP_CONTENT_PADDING_X },
      }}
    >

      {/** STEP HEADER */}
      {!resolvedHideTitle &&
        <Stack spacing={0.5} marginBottom={4} sx={titleSx}>

          <Typography color={GRAY_900} variant='text-xl' fontWeight={600}>
            {title}
          </Typography>

          {subtitle && <Typography variant="text-sm">{subtitle}</Typography>}

        </Stack>
      }

      {/** STEP CONTENT */}
      {children}

    </Stack>
  )
}
